import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import InjectorDetailCard from "@/components/InjectorDetailCard/InjectorDetailCard";
import Nav from "@/components/Header/Nav/Nav";
import Footer from "@/components/Footer/Footer";
import styles from "./InjectorDetailPage.module.scss";
import InjectorCard from "@/components/InjectorCard/InjectorCard";
import { useCart } from "@/hooks/useCart";
import CardSlider from "@/components/Slider/Slider"; // Імпортуємо слайдер
// import { Helmet } from "react-helmet";

interface Injector {
  goid: string;
  name: string;
  descr: string;
  purchaseprice: number;
  exchangeprice: number;
  percent: number;
  files: { image?: { [key: number]: string } };
}

const InjectorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [injector, setInjector] = useState<Injector | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [similarProducts, setSimilarProducts] = useState<Injector[]>([]);
  const { addToCart } = useCart();
  const [active, setActive] = useState(0); // Стан для вибору режиму (typeorder)

  useEffect(() => {
    window.scrollTo(0, 0); // Скролимо в верх сторінки
  }, []);

  useEffect(() => {
    const fetchInjector = async () => {
      if (!id) {
        setError("Invalid injector ID.");
        setLoading(false);
        return;
      }

      try {
        const searchResponse = await fetch(
          `https://crm.hixa.eu/external/goodsapi/?type=good&goid=${id}&legid=1`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer DH345DFGDGb32FDsf03876SDFSdf13`,
              Accept: "application/json",
            },
          }
        );

        const searchData = await searchResponse.json();
        if (!searchData || searchData.data.length === 0) {
          setError("Injector not found");
        } else {
          const injector = searchData.data[0];
          setInjector(injector);
          fetchSimilarProducts(injector.name, injector.goid);
        }
      } catch (error) {
        setError("Error fetching injector details");
      } finally {
        setLoading(false);
      }
    };

    const fetchSimilarProducts = async (name: string, currentGoid: string) => {
      try {
        const response = await fetch("https://crm.hixa.eu/external/goodsapi/", {
          method: "POST",
          headers: {
            Authorization: `Bearer DH345DFGDGb32FDsf03876SDFSdf13`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            type: "good",
            onlyprice: "1",
            start: "0",
            limit: "10",
            search: name,
            legid: "1",
          }),
        });

        const data = await response.json();
        const filtered = data?.data?.filter(
          (item: Injector) => item.goid !== currentGoid
        );

        setSimilarProducts(filtered?.slice(0, 5) || []);
      } catch (err) {
        console.error("Error loading similar products:", err);
      }
    };

    fetchInjector();
  }, [id]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;
  if (!injector) return <p>Injector data is missing.</p>;

  // Обробляємо масив зображень
  const imageUrls = injector.files?.image
    ? Object.values(injector.files.image).slice(0, 4) // Беремо перші три зображення
    : ["/images/Shop/template.webp"]; // Якщо зображення немає, використовуємо шаблон

  // Налаштування слайдера
  const sliderSettings = {
    slidesPerView: 5, // Кількість слайдів, що відображаються одночасно
    spaceBetween: 12, // Відстань між слайдами
    loop: true, // Зациклення слайдів
    breakpoints: {
      1025: {
        slidesPerView: 5,
      },
      478: {
        slidesPerView: 3,
      },
      0: {
        slidesPerView: 2, // Відображення 3 слайдів на мобільних
      },
    },
  };

  // typeorder визначаємо на основі активного стану (0 або 1)
  const typeorder = active === 0 ? "exchpart" : "sellpart";
  // const schemaOrgData = {
  //   "@context": "https://schema.org",
  //   "@type": "Product",
  //   name: injector.name,
  //   description: injector.descr,
  //   sku: injector.goid,
  //   brand: {
  //     "@type": "Brand",
  //     name: "Бренд товару",
  //   },
  //   offers: {
  //     "@type": "Offer",
  //     url: `https://hixa.eu/item/${injector.goid}`,
  //     priceCurrency: "EUR",
  //     price: injector.purchaseprice,
  //     itemCondition: "https://schema.org/NewCondition",
  //     availability: "https://schema.org/InStock",
  //   },
  //   image: "https://example.com/product.jpg",
  // };

  return (
    <div className="pageSection">
      <title>{injector.name}</title>
      <link rel="icon" href="/images/favicon.ico" type="image/x-icon" />
      {/* <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgData)}
        </script>
      </Helmet> */}

      <Nav />
      <InjectorDetailCard
        id={injector.goid}
        name={injector.name}
        purchaseprice={injector.purchaseprice}
        percent={injector.percent}
        descr={injector.descr}
        images={imageUrls} // Передаємо масив зображень
        onAddToCart={(goid, quantity) =>
          addToCart(
            {
              goid,
              name: injector.name,
              price: Number(injector.purchaseprice),

              imageUrl: imageUrls[0], // Використовуємо перше зображення для кошика
              typeorder, // Передаємо typeorder
            },
            quantity
          )
        }
        setActive={setActive} // Передаємо setActive для зміни typeorder
      />

      {/* Слайдер для схожих товарів */}
      {similarProducts.length > 0 && (
        <div className="container">
          <section className={`${styles.similarSection} sideP64`}>
            <h3 className={`brxeHeading sideP24 ${styles.similarTitle}`}>
              Ähnliche Produkte
            </h3>
            <CardSlider settings={sliderSettings}>
              {similarProducts.map((product) => {
                const productImage = product.files?.image
                  ? Object.values(product.files.image)[0]
                  : "/images/Shop/template.webp";

                return (
                  <InjectorCard
                    key={product.goid}
                    goid={product.goid}
                    name={product.name}
                    price={product.purchaseprice}
                    imageUrl={productImage}
                  />
                );
              })}
            </CardSlider>
          </section>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default InjectorDetailPage;
